import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import SugarBabyProfileForm from '@/components/forms/SugarBabyProfileForm';
import { useAuth } from '@/context/auth';
import { createSugarBabyProfile } from '@/services/sugar-profile.service';
import { useToast } from '@/hooks/use-toast';
import { useLanguageUtils } from '@/context/language-context';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Terminal } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client'; // Import supabase client
import { FileWithPath } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid'; // For generating unique file names

const BUCKET_NAME = 'sugar_media'; // Define bucket name

const CreateSugarBabyProfilePage: React.FC = () => {
  const { t } = useTranslation(['sugar', 'forms', 'common']);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getUrlWithLanguage } = useLanguageUtils();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Upload Helper ---
  const uploadFiles = async (files: FileWithPath[], type: 'photos' | 'videos'): Promise<{ url: string; name: string }[] | null> => {
      if (!user || files.length === 0) return [];

      const uploadPromises = files.map(async (file) => {
          const fileExt = file.name.split('.').pop();
          const uniqueFileName = `${uuidv4()}.${fileExt}`;
          const filePath = `public/${user.id}/${type}/${uniqueFileName}`; // Structured path

          const { error: uploadError } = await supabase.storage
              .from(BUCKET_NAME)
              .upload(filePath, file);

          if (uploadError) {
              console.error(`Error uploading ${type}:`, uploadError);
              throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
          }

          // Get public URL (ensure bucket allows public access or adjust policy)
          const { data: urlData } = supabase.storage
              .from(BUCKET_NAME)
              .getPublicUrl(filePath);
          
          return { url: urlData.publicUrl, name: file.name }; // Return URL and original name
      });

      try {
          return await Promise.all(uploadPromises);
      } catch (err) {
          setError((err as Error).message);
          return null;
      }
  };
  // --- End Upload Helper ---

  const handleSubmit = async (formData: any, filesToUpload: FileWithPath[]) => {
    if (!user) return;
    setIsSubmitting(true);
    setError(null);

    try {
        // Separate photos and videos for upload
        const photosToUpload = filesToUpload.filter(f => f.type.startsWith('image/'));
        const videosToUpload = filesToUpload.filter(f => f.type.startsWith('video/'));

        // Upload files concurrently
        const [uploadedPhotos, uploadedVideos] = await Promise.all([
            uploadFiles(photosToUpload, 'photos'),
            uploadFiles(videosToUpload, 'videos'),
        ]);

        // Check if any upload failed
        if (uploadedPhotos === null || uploadedVideos === null) {
            // Error state is already set by uploadFiles helper
            setIsSubmitting(false);
            return;
        }

        // Prepare profile data with media URLs
        // For V1, store all URLs in gallery. Main photo selection can be a later feature.
        const profileData = {
            ...formData,
            photos: uploadedPhotos.length > 0 
                ? { gallery: uploadedPhotos.map(p => p.url), visibility: 'public' } // Default visibility
                : undefined, 
            videos: uploadedVideos.length > 0 
                ? { gallery: uploadedVideos.map(v => v.url), visibility: 'public' }
                : undefined,
        };

        // Create profile with text data and media URLs
        const result = await createSugarBabyProfile(user.id, profileData);

        if (result.success) {
            toast({ title: t('forms:success_profile_created') });
            // TODO: Update user context or refetch user to get the new profile ID
            navigate(getUrlWithLanguage('/sugar-relationships'));
        } else {
            setError(result.error?.message || t('common:error_unknown'));
            toast({ title: t('common:error_title'), description: error, variant: 'destructive' });
        }
    } catch (err: any) {
        console.error("Submission error:", err); // Log the actual error
        setError(err.message || t('common:error_unexpected'));
        toast({ title: t('common:error_title'), description: error, variant: 'destructive' });
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto py-8 max-w-2xl">
        <h1 className="text-2xl font-bold mb-6">{t('sugar:title_create_sb_profile')}</h1>
        {error && (
           <Alert variant="destructive" className="mb-4">
             <Terminal className="h-4 w-4" />
             <AlertTitle>{t('common:error_submission')}</AlertTitle>
             <AlertDescription>{error}</AlertDescription>
           </Alert>
        )}
        {/* Pass the modified handleSubmit */}
        <SugarBabyProfileForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
      </div>
    </Layout>
  );
};

export default CreateSugarBabyProfilePage; 